import { ChangeDetectionStrategy, Component, computed, inject, input, output } from "@angular/core";
import { DatePipe } from "@angular/common";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog } from "@angular/material/dialog";
import {
    ALERT_ACTION_LABELS,
    ALERT_ACTION_SOURCE_LABELS,
    ALERT_CHANNEL_LABELS,
    ALERT_DELIVERY_STATUS_LABELS,
    ALERT_SEVERITY_LABELS,
    ALERT_STATE_LABELS,
    AlertAction,
    AlertActionView,
    AlertDeliveryStatus,
    AlertSeverity,
    AlertView,
    isSupportedAction,
    labelOf,
} from "../alerts.service";
import {
    AlertActionDialogComponent,
    AlertActionDialogData,
} from "../alert-action-dialog/alert-action-dialog.component";

type HistoryEntry = {
    label: string;
    action: AlertActionView;
};

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "app-alert-card",
    templateUrl: "./alert-card.component.html",
    styleUrl: "./alert-card.component.scss",
    imports: [DatePipe, MatButtonModule],
})
export class AlertCardComponent {
    private readonly dialog = inject(MatDialog);

    readonly alert = input.required<AlertView>();
    readonly changed = output<void>();

    readonly severity = computed(() => labelOf(ALERT_SEVERITY_LABELS, this.alert().severity));
    readonly isCritical = computed(() => this.alert().severity === AlertSeverity.Critical);
    readonly state = computed(() => labelOf(ALERT_STATE_LABELS, this.alert().state));
    readonly hasLatestText = computed(() => this.alert().lastText !== this.alert().text);

    readonly history = computed(() => {
        const alert = this.alert();
        const entries: HistoryEntry[] = [];
        if (alert.acknowledged) {
            entries.push({ label: "Acknowledged", action: alert.acknowledged });
        }
        if (alert.ignored) {
            entries.push({ label: "Ignored", action: alert.ignored });
        }
        if (alert.resolved) {
            entries.push({ label: "Resolved", action: alert.resolved });
        }
        return entries.map(({ label, action }) => ({
            // No source: a quiet Warning resolved itself, and "by" says "automatically".
            who:
                action.source === null
                    ? `${label} ${action.by}`
                    : `${label} by ${action.by} (${labelOf(ALERT_ACTION_SOURCE_LABELS, action.source)})`,
            at: action.at,
            comment: action.comment,
        }));
    });

    readonly deliveries = computed(() =>
        this.alert().deliveries.map((delivery) => ({
            // A removed destination has no name in the configuration any more, only the key the delivery keeps.
            destination: delivery.destinationName ?? delivery.destinationKey,
            channel: labelOf(ALERT_CHANNEL_LABELS, delivery.channel),
            status: labelOf(ALERT_DELIVERY_STATUS_LABELS, delivery.status),
            isRetrying: delivery.status === AlertDeliveryStatus.NotDelivered,
            deliveredAt: delivery.status === AlertDeliveryStatus.Delivered ? delivery.deliveredAt : null,
            attempts: delivery.attempts,
            lastError: delivery.lastError,
            hasMovedAddress:
                delivery.originalAddress !== null &&
                delivery.address !== null &&
                delivery.originalAddress !== delivery.address,
            originalAddress: delivery.originalAddress,
            address: delivery.address,
        })),
    );

    readonly actions = computed(() =>
        this.alert()
            .actions.filter(isSupportedAction)
            .map((action) => ({ action, label: ALERT_ACTION_LABELS[action] })),
    );

    openActionDialog(action: AlertAction): void {
        const alert = this.alert();
        this.dialog
            .open<AlertActionDialogComponent, AlertActionDialogData, boolean>(AlertActionDialogComponent, {
                data: { alertId: alert.id, action, subject: alert.subject },
                width: "95vw",
                maxWidth: "560px",
                disableClose: true,
            })
            .afterClosed()
            .subscribe((reload) => {
                if (reload) {
                    this.changed.emit();
                }
            });
    }
}
